import { Selection, select, ScaleTime, EnterElement } from "d3";
import { D3GraphsOverlay } from "../Visualizations/TimeSeriesGraphGroup/D3/D3GraphsOverlay";
import { MarginedBoundingBox } from "../../Types/MarginedBoundingBox";
import { Annotation } from "../../../../../../Managers/VisualizationManager/Variables/Annotations";
import { AnnotationGeometry } from "../Visualizations/TimeSeriesGraphGroup/AnnotationGeometry/AnnotationGeometry";
import { AnnotationGeometryGenerator } from "../Visualizations/TimeSeriesGraphGroup/AnnotationGeometry/AnnotationGeometryGenerator";
import { GraphConfig } from "../../Types/Graph";
import { AnnotationReactCallbacks } from "../../Types/ReactCallbacks";
import { D3OneToManyRenderable } from "./D3OneToManyRenderable";
import { GraphType } from "../../../../../../Enums/GraphType";

export type D3AnnotationsConfig = {
	type: GraphType
	viewScale: ScaleTime<any, any, any>
	annotations: Annotation[]
	boundingBox: MarginedBoundingBox
	graphs: GraphConfig[]
	overlay: D3GraphsOverlay
	clipPathId: string
	canOpenAnnotationsModal: boolean
	annotationZOrder?: number[]
}

export class D3AnnotationsWrapper extends D3OneToManyRenderable<SVGRectElement, D3AnnotationsConfig, AnnotationGeometry, AnnotationReactCallbacks<any>> {
	private geometryGenerator: AnnotationGeometryGenerator
	private geometry: AnnotationGeometry[] = []

	constructor(root: SVGGElement, config: D3AnnotationsConfig, reactCallbacks: AnnotationReactCallbacks<any>) {
		super(root, config, "d3-annotations-wrapper", reactCallbacks)
		this.geometryGenerator = new AnnotationGeometryGenerator(config.type)
		this.mount()
	}

	protected enter(newElements: Selection<EnterElement, AnnotationGeometry, SVGGElement, any>): Selection<SVGRectElement, AnnotationGeometry, SVGGElement, any> {
		const annotations = newElements
            .append("rect")
			.attr("clip-path", `url(#${this.config.clipPathId})`)
            .attr("class", this.className)
			.attr("x", geometry => this.config.viewScale(geometry.annotation.start_time))
			.attr("y", geometry => geometry.y)
			.attr("width", geometry => this.calculateAnnotationWidth(geometry))
			.attr("height", geometry => geometry.height)
			.attr("fill", geometry => geometry.color)
			.attr("opacity", geometry => geometry.opacity)
			
		annotations
			.on("mouseenter", event => this.onHover(event))
			.on("mousemove", event => this.onHover(event))
			.on("mouseleave", event => this.onLeave(event))
			.on("contextmenu", event => this.onContextMenu(event))

		return annotations
	}

	protected update = (updatedAnnotations: Selection<any, AnnotationGeometry, any, any>): Selection<any, any, any, any> => {
		return updatedAnnotations
			.attr("x", geometry => this.config.viewScale(geometry.annotation.start_time))
			.attr("y", geometry => geometry.y)
			.attr("width", geometry => this.calculateAnnotationWidth(geometry))
			.attr("height", geometry => geometry.height)
			.attr("fill", geometry => geometry.color)
			.attr("opacity", geometry => geometry.opacity)
	}

	protected datumIdentifier(datum: AnnotationGeometry): string | number {
		return `${datum.annotation.id}-${datum.y}`
	}

	protected getConfigs(): AnnotationGeometry[] {
		const [startDate, endDate] = this.config.viewScale.domain()
		const startTime = startDate.getTime()
		const endTime = endDate.getTime()

		return this.geometry.filter(geometry => 
			(geometry.annotation.start_time >= startTime && geometry.annotation.end_time <= endTime)
			|| (geometry.annotation.start_time <= startTime && geometry.annotation.end_time >= startTime) 
			|| (geometry.annotation.start_time <= endTime && geometry.annotation.end_time >= endTime))
	}

	protected updateDerivedState = () => {
		// Sort annotations based on z-order (first = back, last = front)
		const sortedAnnotations = this.sortAnnotationsByZOrder(this.config.annotations, this.config.annotationZOrder ?? [])
		
		this.geometry = sortedAnnotations.flatMap(annotation => 
			this.geometryGenerator.generate(annotation, this.config.boundingBox, this.config.viewScale, this.config.graphs))
	}

	private sortAnnotationsByZOrder(annotations: Annotation[], zOrder: number[]): Annotation[] {
		if (zOrder.length === 0) {
			return annotations
		}

		const orderMap = new Map(zOrder.map((id, index) => [id, index]))
		const maxOrder = zOrder.length

		return [...annotations].sort((a, b) =>
			(orderMap.get(a.id) ?? maxOrder) - (orderMap.get(b.id) ?? maxOrder)
		)
	}

	private onHover = (event: any) => {
		select<SVGRectElement, Annotation>(event.target)
			.attr("opacity", annotation => Math.min(1, annotation.opacity + 0.15))

		this.config.overlay.onHover(event)
	}

	private onLeave = (event: any) => {
		select<SVGRectElement, Annotation>(event.target)
			.attr("opacity", annotation => annotation.opacity)

		this.config.overlay.onMouseLeave()
	}

	private onContextMenu = (event: React.MouseEvent) => {
		event.preventDefault()

		if (!this.config.canOpenAnnotationsModal) {
			return
		}

		const geometry = select<SVGRectElement, AnnotationGeometry>(event.target as SVGRectElement).datum()

		if (!geometry) {
			return
		}

		this.reactCallbacks.openAnnotationContextMenu(event, geometry.annotation)
	}

	private calculateAnnotationWidth = (geometry: AnnotationGeometry) => {
		return Math.max(this.config.viewScale(geometry.annotation.end_time) - this.config.viewScale(geometry.annotation.start_time), 5)
	}
}
